import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, RefreshCw, Search, AlertTriangle, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { crmDiagnoseLocationCap, crmResyncLocationCap } from "@/lib/crm.functions";

export const Route = createFileRoute("/crm/diagnostics/locations")({
  component: LocationCapDiagnosticsPage,
});

type ClientReport = {
  client: {
    id: string;
    company_name: string | null;
    contact_name: string | null;
    email: string | null;
    allowed_locations: number | null;
  };
  contracts: Array<{
    id: string;
    allowed_locations: number | null;
    signed_at: string | null;
    starts_at: string | null;
    ends_at: string | null;
    esign_status: string | null;
    value_eur: number | null;
    contract_type: string | null;
  }>;
  contract_max: number;
  expected_cap: number;
  client_cap: number;
  mismatch: boolean;
  onboardings: Array<{
    id: string;
    status: string | null;
    token: string | null;
    updated_at: string | null;
    locations_in_draft: number;
  }>;
};

function LocationCapDiagnosticsPage() {
  const diagnose = useServerFn(crmDiagnoseLocationCap);
  const resync = useServerFn(crmResyncLocationCap);
  const [search, setSearch] = useState("");
  const [reports, setReports] = useState<ClientReport[]>([]);

  const runQ = useMutation({
    mutationFn: async (q: string) => {
      const res = (await diagnose({ data: { search: q } })) as { clients: ClientReport[] };
      return res.clients;
    },
    onSuccess: (rows) => {
      setReports(rows);
      if (rows.length === 0) toast.info("No clients matched that search");
    },
    onError: (e: any) => toast.error(e?.message ?? "Search failed"),
  });

  const resyncM = useMutation({
    mutationFn: async (client_id: string) => resync({ data: { client_id } }) as any,
    onSuccess: (r: any) => {
      toast.success(`Cap updated: ${r.before} → ${r.after}`);
      if (search.trim()) runQ.mutate(search.trim());
    },
    onError: (e: any) => toast.error(e?.message ?? "Resync failed"),
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <MapPin className="h-5 w-5" /> Allowed Locations Diagnostics
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Verify the location cap on each client against their signed contracts before onboarding invites go out.
          If the client cap is below the contract max, resync to fix it in one click.
        </p>
      </div>

      <form
        className="flex flex-wrap items-center gap-2 mb-5"
        onSubmit={(e) => {
          e.preventDefault();
          const q = search.trim();
          if (q) runQ.mutate(q);
        }}
      >
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, contact name, or email"
            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-slate-500"
          />
        </div>
        <button
          type="submit"
          disabled={runQ.isPending || !search.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {runQ.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Diagnose
        </button>
      </form>

      {reports.length === 0 && !runQ.isPending && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          Search a client to inspect their contract-driven location cap.
        </div>
      )}

      <div className="space-y-4">
        {reports.map((r) => (
          <div
            key={r.client.id}
            className={`rounded-xl border bg-white p-4 shadow-sm ${
              r.mismatch ? "border-amber-300" : "border-slate-200"
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {r.client.company_name || r.client.contact_name || "Unnamed client"}
                </div>
                <div className="text-xs text-slate-500">
                  {r.client.email || "—"} · <span className="font-mono">{r.client.id.slice(0, 8)}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-slate-500">Client cap</div>
                  <div className="text-base font-semibold text-slate-900">{r.client_cap}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Expected (from contracts)</div>
                  <div className="text-base font-semibold text-slate-900">{r.expected_cap}</div>
                </div>
                {r.mismatch ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5" /> Mismatch
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
                    <CheckCircle2 className="h-3.5 w-3.5" /> In sync
                  </span>
                )}
                <button
                  onClick={() => resyncM.mutate(r.client.id)}
                  disabled={resyncM.isPending && resyncM.variables === r.client.id}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                >
                  {resyncM.isPending && resyncM.variables === r.client.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Resync cap
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Contracts ({r.contracts.length})
                </div>
                {r.contracts.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500">
                    No contracts on file — cap defaults to 1.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Signed</th>
                          <th className="px-2 py-1.5 text-left">Ends</th>
                          <th className="px-2 py-1.5 text-right">Locations</th>
                          <th className="px-2 py-1.5 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.contracts.map((c) => (
                          <tr key={c.id} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 text-slate-700">
                              {c.signed_at ? new Date(c.signed_at).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-2 py-1.5 text-slate-700">
                              {c.ends_at ? new Date(c.ends_at).toLocaleDateString() : c.contract_type === "open_ended" ? "open" : "—"}
                            </td>
                            <td
                              className={`px-2 py-1.5 text-right font-semibold ${
                                Number(c.allowed_locations ?? 0) === r.contract_max && r.contract_max > 0
                                  ? "text-emerald-700"
                                  : "text-slate-700"
                              }`}
                            >
                              {c.allowed_locations ?? 1}
                            </td>
                            <td className="px-2 py-1.5 text-slate-600">{c.esign_status || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Onboarding drafts ({r.onboardings.length})
                </div>
                {r.onboardings.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500">
                    No onboarding invite issued yet. Cap will apply when the invite email is sent.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Status</th>
                          <th className="px-2 py-1.5 text-left">Updated</th>
                          <th className="px-2 py-1.5 text-right">Locations in draft</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.onboardings.map((o) => (
                          <tr key={o.id} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 text-slate-700">{o.status || "—"}</td>
                            <td className="px-2 py-1.5 text-slate-600">
                              {o.updated_at ? new Date(o.updated_at).toLocaleString() : "—"}
                            </td>
                            <td
                              className={`px-2 py-1.5 text-right font-semibold ${
                                o.locations_in_draft > r.expected_cap ? "text-red-700" : "text-slate-800"
                              }`}
                            >
                              {o.locations_in_draft} / {r.expected_cap}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {r.mismatch && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                Client cap is <b>{r.client_cap}</b> but contracts allow <b>{r.expected_cap}</b>. Click
                <span className="mx-1 font-semibold">Resync cap</span>
                before sending the onboarding invite — the partner form reads from the client record.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
